import Head from "next/head"
import React from "react"
import styled, { css } from "styled-components"

import Link from "~/components/atoms/Link"
import Pc from "~/components/atoms/Pc"
import Sp from "~/components/atoms/Sp"
import Typography from "~/components/atoms/Typography"
import { Container } from "~/components/layouts/Container"
import Breadcrumb from "~/components/organisms/Breadcrumb"
import Footer from "~/components/organisms/Footer"
import Header from "~/components/organisms/Header"
import User from "~/components/organisms/User"
import { Color, TextSize, IsPC, IsMobile } from "~/consts/style-mixins"
import { Company } from "~/entities/Company"
import TabBar from "~/observers/TabBar"
import { staticPath, internalLink } from "~/services/helper"
import RootStore from "~/stores/RootStore"

interface Props {
  companies: Company[]
}

export default class Companies extends React.Component<Props> {
  static async getInitialProps({ initialStoreData }: { initialStoreData: RootStore }) {
    const { companyStore } = initialStoreData
    const companies = await companyStore.fetch()
    return { companies }
  }

  render() {
    const crumbs = [
      {
        name: "インターンシップ・新卒採用情報掲載企業一覧",
        link: internalLink(`/companies`),
      },
    ]
    return (
      <>
        <Head>
          <title>企業一覧 | Infra（インフラ）</title>
          <meta
            name="description"
            content="掲載企業一覧です。Infra（インフラ）では長期インターンシップや短期インターン、説明会 / イベント、新卒採用情報などインターンから就職活動までを一貫してサポートします。"
          />
          <meta property="og:title" content={`企業一覧 | Infra（インフラ）`} />
          <meta property="og:description" content={`Infra（インフラ）では様々な企業の募集を掲載しています。`} />
          <meta property="og:image" content={`${process.env.APP_HOST + staticPath("/static/images/ogp.png")}`} />
        </Head>
        <Header showTab logoType="Plain" />
        <Container size="Medium">
          <StyledInner>
            <Pc>
              <StyledTypography type="SubHero">
                <h1>インターンシップ・新卒採用情報掲載企業一覧</h1>
              </StyledTypography>
            </Pc>
            <Sp>
              <StyledTypography type="SubHero">
                <h1>インターンシップ・新卒採用情報掲載企業一覧</h1>
              </StyledTypography>
            </Sp>
            <StyledRecruitmentNum>全{this.props.companies.length}件の会社</StyledRecruitmentNum>
            <StyledCompanyListWrapper>
              {this.props.companies.map((company, index) => {
                const companyName = company.name.replace("株式会社", "")
                return (
                  <StyledCompanyItemLink key={index}>
                    <Link href={internalLink(`/companies/${company.id}`)}>
                      <User
                        type="xLarge"
                        name={companyName}
                        iconPath={company.logoImgUrl}
                        description={company.overview}
                      />
                    </Link>
                  </StyledCompanyItemLink>
                )
              })}
            </StyledCompanyListWrapper>
          </StyledInner>
        </Container>
        <Breadcrumb crumbs={crumbs} />
        <Sp>
          <TabBar selectedTabIndex={0} />
        </Sp>
        <Footer />
      </>
    )
  }
}

const StyledInner = styled.div`
  ${IsPC(css`
    padding: 48px 0 24px;
    min-height: calc(100vh - 243px);
  `)}
  ${IsMobile(css`
    padding: 24px 0 8px;
    min-height: calc(100vh - 358px);
  `)}
`

const StyledTypography = styled(Typography)`
  margin-bottom: 8px;
`

const StyledRecruitmentNum = styled.div`
  margin-bottom: 24px;
  font-size: ${TextSize.S}px;
  color: ${Color.LightGray};
`

const StyledCompanyListWrapper = styled.div`
  margin: 24px -8px 0;
  display: flex;
  flex-wrap: wrap;
`

const StyledCompanyItemLink = styled.div`
  padding: 0 8px 24px;
  ${IsPC(css`
    width: 25%;
  `)}
  ${IsMobile(css`
    width: 33.3%;
  `)}
`
